import * as React from 'react'
import { FileText } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { sourceFileBasename } from './source-doc-kind'
import type { SourceDocRef } from './useSourceDocViewer'

export interface SourceDocChipProps {
  jobId?: string
  sourceFile?: string
  sourcePage?: number
  sourceExcerpt?: string
  /** Icon + page only; full label lives in the tooltip with the excerpt. */
  compact?: boolean
  className?: string
  onOpen: (args: SourceDocRef) => void
}

function formatChipLabel(sourceFile: string, sourcePage?: number): string {
  const base = sourceFileBasename(sourceFile)
  return sourcePage !== undefined ? `${base} · p. ${sourcePage}` : base
}

/**
 * Opens the shared source-document viewer. Renders nothing unless both
 * `jobId` and `sourceFile` are present so surfaces without provenance stay
 * unchanged.
 */
export function SourceDocChip({
  jobId,
  sourceFile,
  sourcePage,
  sourceExcerpt,
  compact = false,
  className,
  onOpen
}: SourceDocChipProps): React.ReactElement | null {
  if (!jobId || !sourceFile) return null

  const label = formatChipLabel(sourceFile, sourcePage)
  const tooltip = [label, sourceExcerpt?.trim()].filter(Boolean).join('\n')

  const button = (
    <button
      type="button"
      onClick={() => onOpen({ jobId, sourceFile, sourcePage, sourceExcerpt })}
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full border bg-bg-elev font-medium text-ink no-underline hover:border-brand/40 hover:text-brand',
        compact ? 'px-1.5 py-0.5 text-10' : 'px-2.5 py-1 text-12',
        className
      )}
      aria-label={`Open source document ${label}`}
    >
      <FileText aria-hidden className={cn('shrink-0', compact ? 'size-3' : 'size-3.5')} />
      {compact ? (
        sourcePage !== undefined ? (
          <span className="font-mono tabular-nums">p. {sourcePage}</span>
        ) : null
      ) : (
        <span className="min-w-0 truncate font-mono">{label}</span>
      )}
    </button>
  )

  if (!compact && !sourceExcerpt) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent className="max-w-xs whitespace-pre-wrap">{tooltip}</TooltipContent>
    </Tooltip>
  )
}
